// Centralized API client
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** An upload that got no answer within its `timeoutMs`. */
export class UploadTimeoutError extends Error {
  constructor() {
    super('Upload timed out')
    this.name = 'UploadTimeoutError'
  }
}

/** An upload that got no HTTP answer at all: offline, DNS, a dropped socket. */
export class UploadNetworkError extends Error {
  constructor() {
    super('Upload failed: no response')
    this.name = 'UploadNetworkError'
  }
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>
  responseType?: 'json' | 'blob' | 'text'
}

export interface UploadOptions {
  params?: RequestOptions['params']
  /** Bytes of the body sent so far (XHR upload progress). */
  onProgress?: (sentBytes: number, totalBytes: number) => void
  /** The whole body is on the server; what remains is its work. */
  onSent?: () => void
  /** Aborting rejects with a DOMException `AbortError`, as fetch does. */
  signal?: AbortSignal
  /** Give up with `UploadTimeoutError` after this many ms (0 or absent: no limit). */
  timeoutMs?: number
}

const TOKEN_STORAGE_KEY = 'spectre_access_token'

/** Statuses a `Response` must be built with a null body for. */
const NULL_BODY_STATUSES = new Set([101, 103, 204, 205, 304])

const abortError = () => new DOMException('The upload was aborted', 'AbortError')

function parseHeaders(raw: string): Headers {
  const headers = new Headers()
  for (const line of raw.trim().split(/[\r\n]+/)) {
    const colon = line.indexOf(':')
    if (colon > 0) headers.append(line.slice(0, colon).trim(), line.slice(colon + 1).trim())
  }
  return headers
}

/** The finished XHR as a fetch `Response`, so the shared status handling applies to it. */
function xhrResponse(xhr: XMLHttpRequest): Response {
  return new Response(NULL_BODY_STATUSES.has(xhr.status) ? null : xhr.responseText, {
    status: xhr.status,
    statusText: xhr.statusText,
    headers: parseHeaders(xhr.getAllResponseHeaders()),
  })
}

/**
 * POST a form with XMLHttpRequest, the one browser transport that reports upload progress.
 * Settles like fetch: any HTTP status resolves; abort, timeout and a missing answer reject.
 */
function sendXhr(
  url: string,
  body: FormData,
  headers: Record<string, string>,
  options: UploadOptions
): Promise<Response> {
  const { signal } = options
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const xhr = new XMLHttpRequest()
    const onAbort = () => xhr.abort()
    const settle = (outcome: () => void) => {
      signal?.removeEventListener('abort', onAbort)
      outcome()
    }
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.timeout = options.timeoutMs ?? 0
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value)
    xhr.upload.onprogress = event => options.onProgress?.(event.loaded, event.total)
    xhr.upload.onload = () => options.onSent?.()
    // Status 0 means no HTTP answer; `Response` refuses to be built with it.
    xhr.onload = () =>
      settle(() =>
        xhr.status === 0 ? reject(new UploadNetworkError()) : resolve(xhrResponse(xhr))
      )
    xhr.onerror = () => settle(() => reject(new UploadNetworkError()))
    xhr.ontimeout = () => settle(() => reject(new UploadTimeoutError()))
    xhr.onabort = () => settle(() => reject(abortError()))
    signal?.addEventListener('abort', onAbort)
    xhr.send(body)
  })
}

/**
 * Run `request` with a signal that follows `options.signal` and, given a `timeoutMs`, gives up
 * after it with `UploadTimeoutError`, the error the XHR itself times out with.
 */
async function withTimeout<T>(
  options: UploadOptions,
  request: (signal?: AbortSignal) => Promise<T>
): Promise<T> {
  const { signal, timeoutMs } = options
  if (!timeoutMs) return request(signal)
  const controller = new AbortController()
  const forwardAbort = () => controller.abort()
  if (signal?.aborted) forwardAbort()
  signal?.addEventListener('abort', forwardAbort)
  let expired = false
  const timer = setTimeout(() => {
    expired = true
    controller.abort()
  }, timeoutMs)
  try {
    return await request(controller.signal)
  } catch (error) {
    throw expired ? new UploadTimeoutError() : error
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', forwardAbort)
  }
}

/**
 * `sessionStorage` is unavailable outside the browser (e.g. the node-based
 * unit test runner, SSR). Return null there so importing this module and
 * token operations never throw; in the browser this is the real Storage.
 */
function getSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

class ApiClient {
  private accessToken: string | null = null
  private isRefreshing = false
  private refreshPromise: Promise<boolean> | null = null
  // Classification of the most recent failed refresh: 'transient' (network/5xx —
  // keep the session and retry) vs 'definitive' (invalid/expired — log out).
  private lastRefreshError: 'transient' | 'definitive' | null = null

  constructor() {
    // Load token from sessionStorage on init (supports multiple tabs)
    this.accessToken = getSessionStorage()?.getItem(TOKEN_STORAGE_KEY) ?? null
  }

  /**
   * Set access token and persist to sessionStorage
   */
  setToken(token: string) {
    this.accessToken = token
    getSessionStorage()?.setItem(TOKEN_STORAGE_KEY, token)
  }

  /**
   * Clear access token from memory and sessionStorage
   */
  clearToken() {
    this.accessToken = null
    getSessionStorage()?.removeItem(TOKEN_STORAGE_KEY)
  }

  /**
   * Get current access token
   */
  getToken(): string | null {
    return this.accessToken
  }

  /**
   * Decode a JWT's `exp` claim (seconds since epoch). Returns null if the
   * token is missing or malformed — callers treat that as "needs refresh".
   */
  private getTokenExpiry(token: string | null): number | null {
    if (!token) return null
    try {
      const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
      return typeof payload.exp === 'number' ? payload.exp : null
    } catch {
      return null
    }
  }

  /**
   * Return auth headers for a request, proactively refreshing the access token
   * if it's missing or within `skewSeconds` of expiry. Needed for transports
   * (e.g. assistant-transport) that don't run our 401-retry interceptor.
   */
  async getAuthHeaders(skewSeconds = 30): Promise<Record<string, string>> {
    const exp = this.getTokenExpiry(this.accessToken)
    const nowSec = Date.now() / 1000
    if (this.accessToken === null || exp === null || exp - nowSec <= skewSeconds) {
      await this.refreshToken()
    }
    return this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}
  }

  /**
   * Attempt to refresh the access token using refresh cookie
   * Public for use in SSE client
   */
  async refreshToken(): Promise<boolean> {
    // Prevent concurrent refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    this.isRefreshing = true
    this.refreshPromise = (async () => {
      try {
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        })

        if (response.ok) {
          const data = await response.json()
          this.setToken(data.access_token)
          this.lastRefreshError = null
          return true
        }

        // 5xx → auth service likely unavailable: transient, keep the session.
        if (response.status >= 500) {
          this.lastRefreshError = 'transient'
          return false
        }

        // 4xx (expired/invalid refresh) → definitive: clear the session.
        this.lastRefreshError = 'definitive'
        this.clearToken()
        return false
      } catch (error) {
        // Network failure (server unreachable): transient, keep current token.
        console.error('Token refresh failed (network):', error)
        this.lastRefreshError = 'transient'
        return false
      } finally {
        this.isRefreshing = false
        this.refreshPromise = null
      }
    })()

    return this.refreshPromise
  }

  /**
   * Retry a failed request with new token
   */
  private async retryRequest<_T>(url: string, init: RequestInit): Promise<Response> {
    // Add new token to headers
    const headers = new Headers(init.headers)
    if (this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`)
    }

    return fetch(url, {
      ...init,
      headers,
    })
  }
  private buildUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    // Hardcode /api prefix for all requests
    // Vite proxy handles dev routing, ingress handles production routing
    let url = `/api${path}`

    // Add query parameters if provided
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value))
        }
      })
      url += `?${searchParams.toString()}`
    }

    return url
  }

  private async handleResponse<T>(
    response: Response,
    url: string,
    init: RequestInit,
    responseType?: 'json' | 'blob' | 'text'
  ): Promise<T> {
    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401 && this.accessToken) {
      const refreshed = await this.refreshToken()

      if (refreshed) {
        // Retry the original request with new token
        const retryResponse = await this.retryRequest<T>(url, init)
        if (retryResponse.ok) {
          return this.parseResponse<T>(retryResponse, responseType)
        }
        // The fresh token was accepted and the request refused on its merits (403, 404,
        // 503 …): an ordinary error, not a lost session.
        if (retryResponse.status !== 401) {
          throw await this.errorFromResponse(retryResponse)
        }
      }

      // Transient failure (network/5xx): keep the session, surface a
      // non-destructive signal so the UI can show "reconnecting" and retry.
      if (this.lastRefreshError === 'transient') {
        window.dispatchEvent(new CustomEvent('auth:connection-lost'))
        throw new ApiError('Auth service temporarily unavailable', 503, {
          detail: 'Connection lost',
        })
      }

      // Definitive failure: the session is gone — log out.
      this.sessionExpired()
    }

    if (!response.ok) {
      throw await this.errorFromResponse(response)
    }

    return this.parseResponse<T>(response, responseType)
  }

  /** The session is gone for good: sign the operator out and fail the request. */
  private sessionExpired(): never {
    this.clearToken()
    window.dispatchEvent(new CustomEvent('auth:unauthorized'))
    throw new ApiError('Authentication required', 401, { detail: 'Session expired' })
  }

  /** A non-ok response as an `ApiError` carrying its JSON (or text) body. */
  private async errorFromResponse(response: Response): Promise<ApiError> {
    let errorData: unknown
    const responseClone = response.clone()
    try {
      errorData = await response.json()
    } catch {
      errorData = await responseClone.text()
    }
    return new ApiError(
      `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      errorData
    )
  }

  /**
   * Parse response based on content type or explicit responseType
   */
  private async parseResponse<T>(
    response: Response,
    responseType?: 'json' | 'blob' | 'text'
  ): Promise<T> {
    // Handle 204 No Content - return undefined as T (typically void)
    if (response.status === 204) {
      return undefined as T
    }

    // If explicit responseType is provided, use it
    if (responseType === 'blob') {
      return response.blob() as T
    }
    if (responseType === 'text') {
      return response.text() as T
    }
    if (responseType === 'json') {
      return response.json()
    }

    // Otherwise, detect from content-type header
    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
      return response.json()
    }

    if (contentType?.includes('application/pdf') || contentType?.includes('blob')) {
      return response.blob() as T
    }

    return response.text() as T
  }

  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { params, responseType, ...init } = options
    const url = this.buildUrl(path, params)

    // If no access token but we might have refresh cookie, try to refresh first
    if (!this.accessToken && !url.includes('/auth/')) {
      await this.refreshToken()
    }

    const requestInit: RequestInit = {
      ...init,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        ...init.headers,
      },
      credentials: 'include', // Send cookies for refresh token
    }

    const response = await fetch(url, requestInit)
    return this.handleResponse<T>(response, url, requestInit, responseType)
  }

  async post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...init } = options
    const url = this.buildUrl(path, params)

    // If no access token but we might have refresh cookie, try to refresh first
    if (!this.accessToken && !url.includes('/auth/')) {
      await this.refreshToken()
    }

    const isFormData = body instanceof FormData

    const requestInit: RequestInit = {
      ...init,
      method: 'POST',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        ...init.headers,
      },
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    }

    const response = await fetch(url, requestInit)
    return this.handleResponse<T>(response, url, requestInit)
  }

  /**
   * POST a form over XHR for byte progress, abort and a timeout. The token is refreshed up
   * front, because a 401 retry would send the whole body again without progress; the answer
   * then goes through the same status and 401 handling as every other request. A refresh
   * that was refused signs the operator out before anything is sent, as `post()` does; the
   * 401 retry keeps the upload's `timeoutMs`.
   */
  async upload<T>(path: string, body: FormData, options: UploadOptions = {}): Promise<T> {
    const url = this.buildUrl(path, options.params)
    const headers = await this.getAuthHeaders()
    // A null token always refreshes above, so `lastRefreshError` is this attempt's outcome.
    if (!headers.Authorization && this.lastRefreshError === 'definitive') this.sessionExpired()
    const response = await sendXhr(url, body, headers, options)
    return withTimeout(options, signal =>
      this.handleResponse<T>(response, url, {
        method: 'POST',
        body,
        headers,
        credentials: 'include',
        signal,
      })
    )
  }

  async put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...init } = options
    const url = this.buildUrl(path, params)

    // If no access token but we might have refresh cookie, try to refresh first
    if (!this.accessToken && !url.includes('/auth/')) {
      await this.refreshToken()
    }

    const isFormData = body instanceof FormData

    const requestInit: RequestInit = {
      ...init,
      method: 'PUT',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        ...init.headers,
      },
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    }

    const response = await fetch(url, requestInit)
    return this.handleResponse<T>(response, url, requestInit)
  }

  async patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...init } = options
    const url = this.buildUrl(path, params)

    // If no access token but we might have refresh cookie, try to refresh first
    if (!this.accessToken && !url.includes('/auth/')) {
      await this.refreshToken()
    }

    const requestInit: RequestInit = {
      ...init,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        ...init.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    }

    const response = await fetch(url, requestInit)
    return this.handleResponse<T>(response, url, requestInit)
  }

  async delete<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...init } = options
    const url = this.buildUrl(path, params)

    // If no access token but we might have refresh cookie, try to refresh first
    if (!this.accessToken && !url.includes('/auth/')) {
      await this.refreshToken()
    }

    const requestInit: RequestInit = {
      ...init,
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        ...init.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    }

    const response = await fetch(url, requestInit)
    return this.handleResponse<T>(response, url, requestInit)
  }
}

// Export configured client instance
export const apiClient = new ApiClient()
